import { useEffect, useState, useMemo } from "react";
import Topheader from "../../../Layout/header/header";
import "./application-summary.css";
import { SideMenuReports } from "./sidemenu.jsx";
import styles from "../../../Components/Table.module.css";
import Analytics from "./analytics-global-admin.jsx";
import APIManager from "../../../Services/ApiManager/ApiManager.js";
import PopupDialog from "../../../Components/PopupDialog.jsx";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Tooltip,
  CircularProgress,
  Backdrop,
  Collapse,
  IconButton,
  Chip,
  TableCell,
  TablePagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PropTypes from "prop-types";

// Helper function to render sort icon based on current sort state
const renderSortIcon = (columnId, orderBy, order) => {
  if (orderBy === columnId) {
    return (
      <span style={{ marginLeft: "4px" }}>
        {order === "asc" ? (
          <ArrowUpwardIcon sx={{ fontSize: "14px", color: "#12141599" }} />
        ) : (
          <ArrowDownwardIcon sx={{ fontSize: "14px", color: "#12141599" }} />
        )}
      </span>
    );
  }
  return (
    <span style={{ marginLeft: "4px", opacity: 0.3 }}>
      <ArrowUpwardIcon sx={{ fontSize: "14px", color: "#12141599" }} />
    </span>
  );
};

// Helper function to truncate category
const getTruncatedCategory = (categories, maxLength = 17) => {
  if (!categories?.[0]) return "N/A";
  return categories[0].length > maxLength
    ? `${categories[0].substring(0, maxLength)}...`
    : categories[0];
};

// Helper function to truncate app version
const getTruncatedVersion = (version, maxLength = 14) => {
  if (!version) return "N/A";
  return version.length > maxLength
    ? `${version.substring(0, maxLength)}...`
    : version;
};

const DATE_SORT_FIELDS = new Set([
  "lastModifiedAt",
  "submittedAt",
  "approvedAt",
  "rejectedAt",
  "publishedAt",
]);

const getSortValue = (item, field) => {
  if (field === "category") {
    return item.selectedCategories?.[0] || "";
  }
  const value = item[field] || "";
  if (DATE_SORT_FIELDS.has(field)) {
    return value ? new Date(value).getTime() : 0;
  }
  if (field === "apiLevel") {
    return Number.parseInt(value) || 0;
  }
  return value;
};

const compareValues = (aValue, bValue, order) => {
  if (typeof aValue === "string" && typeof bValue === "string") {
    const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
    return order === "asc" ? comparison : -comparison;
  }
  if (aValue < bValue) return order === "asc" ? -1 : 1;
  if (aValue > bValue) return order === "asc" ? 1 : -1;
  return 0;
};

const CollapseToggleSVG = ({ collapsed }) =>
  collapsed ? (
    <svg width="8" height="9" viewBox="0 0 8 9" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 -0.000163078L0 3.99984L0.933333 4.93317L3.33333 2.53317V8.6665H4.66667V2.53317L7.06667 4.93317L8 3.99984L4 -0.000163078Z" fill="#0476E2" />
    </svg>
  ) : (
    <svg width="8" height="9" viewBox="0 0 8 9" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 8.66667L0 4.66667L0.933333 3.73333L3.33333 6.13333V0H4.66667V6.13333L7.06667 3.73333L8 4.66667L4 8.66667Z" fill="#0476E2" />
    </svg>
  );

CollapseToggleSVG.propTypes = {
  collapsed: PropTypes.bool.isRequired,
};

const ChipListWithToggle = ({ items, isExpanded, onToggle, emptyMessage }) => {
  const MAX_VISIBLE = 16;
  if (!items || items.length === 0) {
    return (
      <Typography sx={{ fontSize: "14px", color: "#9CA3AF" }}>
        {emptyMessage}
      </Typography>
    );
  }
  const visibleItems = isExpanded ? items : items.slice(0, MAX_VISIBLE);
  return (
    <>
      {visibleItems.map((item) => (
        <Chip
          key={item}
          label={item}
          sx={{ backgroundColor: "#F3F3F3", fontSize: "12px", height: "28px", borderRadius: "4px" }}
        />
      ))}
      {items.length > MAX_VISIBLE && (
        <Button
          size="small"
          sx={{
            fontSize: "12px",
            border: "1px solid #0476E2",
            borderRadius: "4px",
            textTransform: "none",
            display: "flex",
            alignItems: "center",
            gap: "4px",
            height: "24px",
          }}
          onClick={onToggle}
        >
          {isExpanded ? "Collapse" : `${items.length - MAX_VISIBLE} More`}{" "}
          <CollapseToggleSVG collapsed={isExpanded} />
        </Button>
      )}
    </>
  );
};

ChipListWithToggle.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string),
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  emptyMessage: PropTypes.string.isRequired,
};

const DATE_FIELDS = [
  { label: "Submitted On", key: "submittedAt" },
  { label: "Approved On", key: "approvedAt" },
  { label: "Rejected On", key: "rejectedAt" },
  { label: "Published On", key: "publishedAt" },
];

const ExpandedRowContent = ({
  row,
  expandedMakes,
  expandedCountries,
  handleMakeExpand,
  handleCountryExpand,
  formatDate,
}) => (
  <Box sx={{ padding: "24px", backgroundColor: "#f8f9fa" }}>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: "24px",
        marginBottom: "24px",
      }}
    >
      {DATE_FIELDS.map(({ label, key }) => (
        <Box key={label}>
          <Typography sx={{ fontSize: "12px", color: "#5A5B5C", marginBottom: "4px", fontWeight: "600" }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#2A2C2D" }}>
            {formatDate(row[key])}
          </Typography>
        </Box>
      ))}
    </Box>

    <Box sx={{ marginBottom: "24px" }}>
      <Typography sx={{ fontSize: "12px", color: "#5A5B5C", marginBottom: "8px", fontWeight: "600" }}>
        Targeted Makes
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
        <ChipListWithToggle
          items={row.targetedMakes}
          isExpanded={!!expandedMakes[row.id]}
          onToggle={() => handleMakeExpand(row.id)}
          emptyMessage="No makes specified"
        />
      </Box>
    </Box>

    <Box sx={{ marginBottom: "24px" }}>
      <Typography sx={{ fontSize: "12px", color: "#5A5B5C", marginBottom: "8px", fontWeight: "600" }}>
        Targeted Countries
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
        <ChipListWithToggle
          items={row.targetedCountries}
          isExpanded={!!expandedCountries[row.id]}
          onToggle={() => handleCountryExpand(row.id)}
          emptyMessage="No countries specified"
        />
      </Box>
    </Box>

    <Box>
      <Typography sx={{ fontSize: "12px", color: "#5A5B5C", marginBottom: "8px", fontWeight: "600" }}>
        Languages
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
        {row.languages && row.languages.length > 0 ? (
          row.languages.map((language) => (
            <Chip
              key={language}
              label={language}
              sx={{ backgroundColor: "#F3F3F3", fontSize: "12px", height: "28px", borderRadius: "4px" }}
            />
          ))
        ) : (
          <Typography sx={{ fontSize: "14px", color: "#9CA3AF" }}>
            No languages specified
          </Typography>
        )}
      </Box>
    </Box>
  </Box>
);

ExpandedRowContent.propTypes = {
  row: PropTypes.object.isRequired,
  expandedMakes: PropTypes.object.isRequired,
  expandedCountries: PropTypes.object.isRequired,
  handleMakeExpand: PropTypes.func.isRequired,
  handleCountryExpand: PropTypes.func.isRequired,
  formatDate: PropTypes.func.isRequired,
};

const ApplicationTableRow = ({
  row,
  isExpanded,
  expandedMakes,
  expandedCountries,
  onExpand,
  handleMakeExpand,
  handleCountryExpand,
  getStatusBadge,
  formatDate,
  tableStyles,
}) => {
  const cellBorder = isExpanded ? "none" : "1px solid rgba(224, 224, 224, 1)";
  const statusBadge = getStatusBadge(row.status);

  return (
    <>
      <tr
        className={isExpanded ? "table-row-reports expanded-row" : "table-row-reports"}
        style={{ cursor: "pointer" }}
      >
        <td style={{ padding: "6px", textAlign: "center", borderBottom: cellBorder }}>
          {(row.iconUrl || row.icon) && (
            <img
              src={row.iconUrl || row.icon}
              alt={row.applicationName || "App Icon"}
              width="40"
              height="40"
              style={{ borderRadius: "8px", objectFit: "cover" }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          )}
        </td>
        <td style={{ padding: "8px 8px 8px 5px", borderBottom: cellBorder }}>
          <Typography sx={{ fontSize: "14px", fontWeight: isExpanded ? "bold" : "600", color: "#2A2C2D" }}>
            {row.appName || "Untitled"}
          </Typography>
        </td>
        <td style={{ padding: "8px 8px 8px 5px", borderBottom: cellBorder }}>
          <Tooltip
            title={row.appVersion && row.appVersion.length > 14 ? row.appVersion : ""}
            placement="top"
            arrow
          >
            <Typography sx={{ fontSize: "14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {getTruncatedVersion(row.appVersion)}
            </Typography>
          </Tooltip>
        </td>
        <td style={{ padding: "8px 8px 8px 5px", borderBottom: cellBorder }}>
          <span className={statusBadge.className}>{statusBadge.text}</span>
        </td>
        <td style={{ padding: "8px 8px 8px 5px", borderBottom: cellBorder }}>
          <Tooltip
            title={
              row.selectedCategories?.[0] && row.selectedCategories[0].length > 17
                ? row.selectedCategories[0]
                : ""
            }
            placement="top"
            arrow
          >
            <Typography sx={{ fontSize: "14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {getTruncatedCategory(row.selectedCategories)}
            </Typography>
          </Tooltip>
        </td>
        <td style={{ textAlign: "center", padding: "8px 8px 8px 5px", borderBottom: cellBorder }}>
          <Typography sx={{ fontSize: "14px" }}>{row.apiLevel || "N/A"}</Typography>
        </td>
        <td style={{ padding: "8px 8px 8px 5px", borderBottom: cellBorder }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "14px" }}>{formatDate(row.lastModifiedAt)}</Typography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onExpand(row.id);
              }}
              sx={{ padding: "4px" }}
            >
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Box>
        </td>
      </tr>
      <tr
        style={{
          borderBottom: isExpanded ? "1px solid rgba(224, 224, 224, 1)" : "none",
          backgroundColor: isExpanded ? "#f8f9fa" : "transparent",
        }}
      >
        <TableCell colSpan={7} sx={{ padding: 0 }}>
          <Collapse in={isExpanded}>
            <ExpandedRowContent
              row={row}
              expandedMakes={expandedMakes}
              expandedCountries={expandedCountries}
              handleMakeExpand={handleMakeExpand}
              handleCountryExpand={handleCountryExpand}
              formatDate={formatDate}
            />
          </Collapse>
        </TableCell>
      </tr>
    </>
  );
};

ApplicationTableRow.propTypes = {
  row: PropTypes.object.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  expandedMakes: PropTypes.object.isRequired,
  expandedCountries: PropTypes.object.isRequired,
  onExpand: PropTypes.func.isRequired,
  handleMakeExpand: PropTypes.func.isRequired,
  handleCountryExpand: PropTypes.func.isRequired,
  getStatusBadge: PropTypes.func.isRequired,
  formatDate: PropTypes.func.isRequired,
  tableStyles: PropTypes.object.isRequired,
};

export default function ApplicationSummary() {
  const [activeNavItem, setActiveNavItem] = useState("Application Summary");
  const [tableauOpen, setTableauOpen] = useState(false);
  const [applicationSummaryData, setApplicationSummaryData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [componentName, setComponentName] = useState("");
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [headerMessage, setHeaderMessage] = useState("");
  const [loader, setLoader] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedCountries, setExpandedCountries] = useState({});
  const [expandedMakes, setExpandedMakes] = useState({});

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
 const readOnly_GlobalAdmin =
    sessionStorage.getItem("readonly_GlobalAdmin") === "true";
  const handleActiveItemChange = async (itemName) => {
    const navItemMap = {
      applicationSummary: "Application Summary",
      activity: "activity",
      device: "device",
      applications: "applications",
      topApplications: "topApplications",
      vehicleReport: "vehicleReport",
      storeMetrics: "storeMetrics",
      dataConsumption: "dataConsumption",
      paymentsSummary: "Payments Summary",
      subscriptions: "Subscriptions",
      inAppPurchase: "In-app Purchase",
      appCrash: "appCrash",
      featuresActivity: "featuresActivity",
    };

    const displayName = navItemMap[itemName] || itemName;
    setActiveNavItem(displayName);
  };
  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    setPage(0); // Reset to first page on search

    const queryLowerCase = query.toLowerCase();

    const filtered = applicationSummaryData.filter((app) =>
      app.appName?.toLowerCase().includes(queryLowerCase)
    );
    setFilteredData(filtered);
  };



  const handleExportToExcel = async () => {
    try {
      setLoader(true);
      const response = await APIManager.postExportToExcel();

      if (response?.url) {
        const fileName = response.fileName || "report.xlsx";
        const link = document.createElement("a");
        link.download = fileName;
        link.href = response.url;
        link.click();
      }

      setLoader(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setComponentName("appError");
      setErrorMessage(error.message);
      setHeaderMessage(
        error.message === "jwt expired" ? "401 Token Expired" : "Error"
      );
      setOpenErrorPopup(true);
      setLoader(false);
    }
  };
  const formatDate = (dateString) => {
    if (!dateString) return "---";
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    const isDesc = orderBy === property && order === "desc";

    if (isDesc) {
      setOrder("asc");
      setOrderBy("");
    } else if (isAsc) {
      setOrder("desc");
      setOrderBy(property);
    } else {
      setOrder("asc");
      setOrderBy(property);
    }
  };

  const sortData = (data, orderBy, order) => {
    if (!orderBy) return data;
    return [...data].sort((a, b) =>
      compareValues(getSortValue(a, orderBy), getSortValue(b, orderBy), order)
    );
  };

  const paginatedData = useMemo(() => {
    const sortedData = sortData(filteredData, orderBy, order);
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [filteredData, page, rowsPerPage, orderBy, order]);

  const handleRowExpand = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };
  const handleCountryExpand = (rowId) => {
    setExpandedCountries((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const handleMakeExpand = (rowId) => {
    setExpandedMakes((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      DRAFT: {
        className: styles.draftStatusBadge,
        text: "Draft",
      },
      PUBLISHED: {
        className: styles.publishedStatusBadge,
        text: "Published",
      },
      REJECTED_FOR_PRODUCTION: {
        className: styles.delistedStatusBadge,
        text: "Rejected",
      },
      APPROVED_FOR_PRODUCTION: {
        className: styles.submittedStatusBadge,
        text: "Approved",
      },
      APPROVED_FOR_BETA: {
        className: styles.submittedStatusBadge,
        text: "Approved",
      },
      APPROVED_FOR_STAGING: {
        className: styles.submittedStatusBadge,
        text: "Approved",
      },
      IN_REVIEW: {
        className: styles.inReviewStatusBadge,
        text: "In Review",
      },
      GENERATING_DELTA: {
        className: styles.inReviewStatusBadge,
        text: "Generating Delta",
      },
      DELISTED_BY_ADMIN: {
        className: styles.delistedStatusBadge,
        text: "Delisted by Admin",
      },
      DELISTED_BY_SELLER: {
        className: styles.delistedStatusBadge,
        text: "Delisted by Seller",
      },
      DELISTED_BY_APPROVER: {
        className: styles.delistedStatusBadge,
        text: "Delisted by Approver",
      },
      SUBMITTED_FOR_PRODUCTION: {
        className: styles.submittedStatusBadge,
        text: "Submitted",
      },
      SUBMITTED_FOR_STAGING: {
        className: styles.submittedStatusBadge,
        text: "Submitted",
      },
      SUBMITTED_FOR_BETA: {
        className: styles.submittedStatusBadge,
        text: "Submitted",
      },
      DELETED: {
        className: styles.delistedStatusBadge,
        text: "Deleted",
      },
      DOMAIN_APPROVAL_IN_REVIEW: {
        className: styles.inReviewStatusBadge,
        text: "In Review",
      },
      DOMAIN_APPROVAL_APPROVED: {
        className: styles.submittedStatusBadge,
        text: "Approved",
      },
      REJECTED_BY_DOMAIN_APPROVER: {
        className: styles.delistedStatusBadge,
        text: "Rejected",
      },
    };

    return (
      statusConfig[status] || {
        className: styles.defaultStatusBadge,
        text: status || "Unknown",
      }
    );
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };
  useEffect(() => {
    setLoader(true);
    APIManager.getApplicationSummaryGlobalAdmin()
      .then((response) => {
        const processedData = response.map((app, index) => ({
          ...app,
          id: app.id || app.applicationId || index + 1,
        }));
        setApplicationSummaryData(processedData);
        setFilteredData(processedData);
        setLoader(false);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setComponentName("appError");
        setErrorMessage(error.message);
        setHeaderMessage(
          error.message === "jwt expired" ? "401 Token Expired" : "Error"
        );
        setOpenErrorPopup(true);
        setLoader(false);
      });
  }, []);
  return (
    <>
      <Topheader />
      <Backdrop
        open={loader}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: "#fff" }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <div className="reports-container">
        {activeNavItem === "Application Summary" ? (
          <>
            <div className="reports-side-menu">
              <SideMenuReports
                activeNavItem={activeNavItem}
                handleActiveItemChange={handleActiveItemChange}
                tableauOpen={tableauOpen}
                setTableauOpen={setTableauOpen}
              />
            </div>

            <Box className="application-summary-container">
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
                width="97%"
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: "20px", fontWeight: "500" }}>
                    Applications Summary
                  </Typography>
                  <svg
                    width="5"
                    height="2"
                    viewBox="0 0 5 2"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M4.13281 0V1.5H0V0H4.13281Z" fill="#5A5B5C" />
                  </svg>

                  <Typography sx={{ fontSize: "16px", color: "#5A5B5C" }}>
                    {filteredData.length}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <TextField
                    placeholder="Search for application name"
                    variant="outlined"
                    size="small"
                    value={searchQuery}
                    onChange={handleSearch}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: "#666" }} />
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{
                      width: "300px",
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "4px",
                      },
                    }}
                  />
                  <Button
                    variant="outlined"
                    sx={{
                      textTransform: "none",
                      borderColor: "#1976d2",
                      color: "#1976d2",
                      fontWeight: "500",
                      "&:hover": {
                        borderColor: "#1565c0",
                        backgroundColor: "rgba(25, 118, 210, 0.04)",
                      },
                    }}
                    disabled={readOnly_GlobalAdmin}
                    onClick={handleExportToExcel}
                  >
                    Export To Excel
                  </Button>
                </Box>
              </Box>

              {/* Table with expandable rows */}
              <div
                style={{
                  width: "97%",
                  border: "1px solid rgba(224, 224, 224, 1)",
                  borderRadius: "4px",
                  overflow: "hidden",
                }}
              >
                <table
                  className={styles.applicationSummaryTable}
                  style={{ width: "100%", borderCollapse: "collapse" }}
                >
                  <thead className={styles.tableHeaderCls}>
                    <tr style={{ backgroundColor: "#f0f0f0" }}>
                      <th
                        style={{
                          width: "60px",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#121415",
                          padding: "8px",
                          textAlign: "left",
                        }}
                      ></th>
                      <th
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#121415",
                          padding: "8px 8px 8px 5px",
                          textAlign: "left",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          border: "none",
                          height: "22px",
                        }}
                        onClick={() => handleRequestSort("appName")}
                      >
                        Application Name
                        {renderSortIcon("appName", orderBy, order)}
                      </th>
                      <th
                        style={{
                          width: "120px",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#121415",
                          padding: "8px 8px 8px 5px",
                          textAlign: "left",
                          cursor: "pointer",
                        }}
                        onClick={() => handleRequestSort("appVersion")}
                      >
                        Version
                        {renderSortIcon("appVersion", orderBy, order)}
                      </th>
                      <th
                        style={{
                          width: "135px",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#121415",
                          padding: "8px 8px 8px 5px",
                          textAlign: "left",
                          cursor: "pointer",
                        }}
                        onClick={() => handleRequestSort("status")}
                      >
                        Status
                        {renderSortIcon("status", orderBy, order)}
                      </th>
                      <th
                        style={{
                          width: "120px",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#121415",
                          padding: "8px 8px 8px 5px",
                          textAlign: "left",
                          cursor: "pointer",
                        }}
                        onClick={() => handleRequestSort("category")}
                      >
                        Category
                        {renderSortIcon("category", orderBy, order)}
                      </th>
                      <th
                        style={{
                          width: "80px",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#121415",
                          padding: "8px",
                          textAlign: "center",
                          cursor: "pointer",
                        }}
                        onClick={() => handleRequestSort("apiLevel")}
                      >
                        API
                        {renderSortIcon("apiLevel", orderBy, order)}
                      </th>
                      <th
                        style={{
                          width: "150px",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#121415",
                          padding: "8px 8px 8px 5px",
                          textAlign: "left",
                          cursor: "pointer",
                        }}
                        onClick={() => handleRequestSort("lastModifiedAt")}
                      >
                        Last Modified
                        {renderSortIcon("lastModifiedAt", orderBy, order)}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.map((row) => (
                      <ApplicationTableRow
                        key={row.id}
                        row={row}
                        isExpanded={!!expandedRows[row.id]}
                        expandedMakes={expandedMakes}
                        expandedCountries={expandedCountries}
                        onExpand={handleRowExpand}
                        handleMakeExpand={handleMakeExpand}
                        handleCountryExpand={handleCountryExpand}
                        getStatusBadge={getStatusBadge}
                        formatDate={formatDate}
                        tableStyles={styles}
                      />
                    ))}
                  </tbody>
                </table>

                <TablePagination
                  rowsPerPageOptions={[25, 50, 100]}
                  component="div"
                  count={filteredData.length}
                  rowsPerPage={rowsPerPage}
                  labelRowsPerPage="Items per page:"
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{
                    borderTop: "1px solid rgba(224, 224, 224, 1)",
                    "& .MuiTablePagination-toolbar": {
                      minHeight: "52px",
                    },
                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                      {
                        fontSize: "14px",
                        color: "#5A5B5C",
                      },
                  }}
                />
              </div>
            </Box>
          </>
        ) : (
          <Analytics
            initialNavItem={activeNavItem}
            onNavigateToSummary={handleActiveItemChange}
          />
        )}
      </div>

      <PopupDialog
        openErrorPopup={openErrorPopup}
        errorMessage={errorMessage}
        headerMessage={headerMessage}
        componentName={componentName}
        onClose={() => setOpenErrorPopup(false)}
      />
    </>
  );
}
